import {httpClientV2} from "../../../../infrastructure/http";
import {SalesInsight} from "../../domain/sales-insight.entity";
import {AttendeeDetails} from "../../domain/attendee-details.entity";
import {SalesInsightMapperV2} from "../mappers/sales-insight.mapper.v2";
import {AttendeeAnalyticsMapperV2} from "../mappers/attendee-analytics.mapper.v2";
import {AttendeeDetailsMapperV2} from "../mappers/attendee-details.mapper.v2";
import {
  V2SalesInsightsResponse,
  V2AttendeesResponse,
} from "../mappers/sales-insight.mapper.types";
import {
  ILiveEventAnalyticsRepository,
  GetAttendeesResult,
  GetAttendeeDetailsParams,
  ExportAttendeesCSVParams,
} from "./live-event-analytics.repository";

/**
 * V2 API implementation of ILiveEventAnalyticsRepository.
 *
 * Endpoints:
 * - GET analytics/live-events/sales-insights/:eventId
 * - GET analytics/live-events/attendees/:eventId?page=&perPage=
 *
 * Note: V2 has no per-attendee details endpoint. getAttendeeDetails returns
 * the wrapped inlineAttendee with no network call.
 *
 * Pure TypeScript - no React imports.
 */
export class LiveEventAnalyticsRepositoryV2 implements ILiveEventAnalyticsRepository {
  private readonly basePath = "analytics/live-events";

  async getSalesInsights(eventId: string | number): Promise<SalesInsight> {
    const response = await httpClientV2.get<V2SalesInsightsResponse>(
      `${this.basePath}/sales-insights/${eventId}`
    );

    return SalesInsightMapperV2.toDomain(response.data);
  }

  async getAttendees(
    eventId: string | number,
    page: number,
    perPage: number
  ): Promise<GetAttendeesResult> {
    const response = await httpClientV2.get<V2AttendeesResponse>(
      `${this.basePath}/attendees/${eventId}?page=${page}&perPage=${perPage}`
    );

    return AttendeeAnalyticsMapperV2.toAttendeesResult(response.data, page, perPage);
  }

  async getAttendeeDetails(params: GetAttendeeDetailsParams): Promise<AttendeeDetails> {
    const {inlineAttendee} = params;

    if (!inlineAttendee) {
      throw new Error(
        "V2 repository requires inlineAttendee - no per-attendee endpoint exists"
      );
    }

    return AttendeeDetailsMapperV2.fromInlineAttendee(inlineAttendee);
  }

  async exportAttendeesCSV(params: ExportAttendeesCSVParams): Promise<Blob> {
    const {eventId, startDate, endDate} = params;

    const queryParams = new URLSearchParams();
    queryParams.append("startDate", startDate ?? "2020-01-01");
    queryParams.append("endDate", endDate ?? new Date().toISOString().slice(0, 10));

    const response = await httpClientV2.get<Blob>(
      `${this.basePath}/attendees/export/${eventId}?${queryParams.toString()}`,
      {responseType: "blob"}
    );

    return response;
  }
}
